import { Template, Component, Attribute } from '@scoutgg/ftw/source/lib'
import { prompt } from '@scoutgg/ftw/source/services/modal'
import { Match, Event } from '../../models'
import camelCase from 'camelcase'
import template from './time-trial-results.jade'
import sources from '../../../helpers/sources'
import '../status'
import '../match-times'
import '../lineups'
import getState from '../../services/create-event'
import notify from '@scoutgg/ftw/source/services/notification'
import emitter from '@scoutgg/ftw/source/services/emitter'
import normalize from '../../../helpers/normalize'
import '@scoutgg/ftw/source/atoms/form-control/form-control'

const eventTypes = {
  alpine_downhill: [
    'downhill_time',
    'downhill_speed',
    'downhill_split_time',
  ],
  alpine_combined: [
    'combined_time',
    'combined_downhill_time',
    'combined_downhill_speed',
    'combined_slalom_time',
  ],
  cycling: [
    'time',
    'yellow_shirt',
    'green_shirt',
    'climber_shirt',
    'youth_shirt',
    'aggressive',
    'auxiliary1',
    'auxiliary2',
    'auxiliary3',
    'ping',
  ],
  cycling_team: [
    'time',
    'yellow_shirt',
    'green_shirt',
    'climber_shirt',
    'youth_shirt',
    'aggressive',
    'ping',
  ],
  cc_ski_sprint: [
    'time',
  ],
  cc_ski_team_sprint: [
    'time',
    'split_time',
  ],
  cc_ski_mass_start_women: [
    'time',
    'split_time',
    'split_time2',
  ],
  cc_ski_mass_start_men: [
    'time',
    'split_time',
    'split_time2',
    'split_time3',
    'split_time4',
  ],
  cc_ski_tour: [
    'time',
  ],
  biathlon_relay: [
    'time',
  ],
  biathlon_relay_stage: [
    'time',
    'shooting_relay',
  ],
  biathlon_mixed_relay: [
    'time',
  ],
  biathlon_mixed_relay_stage: [
    'time',
    'shooting_relay',
  ],
  biathlon_sprint: [
    'time',
    'split_time',
    'split_time2',
    'shooting_other',
  ],
  biathlon_pursuit: [
    'time',
    'split_time',
    'split_time2',
    'split_time3',
    'split_time4',
    'shooting_other',
  ],
  biathlon_individual: [
    'time',
    'split_time',
    'split_time2',
    'split_time3',
    'split_time4',
    'shooting_other',
  ],
  biathlon_single_mixed: [
    'time',
  ],
  biathlon_single_mixed_stage: [
    'time',
    'shooting_other',
  ],
  biathlon_mass_start: [
    'time',
    'split_time',
    'split_time2',
    'split_time3',
    'split_time4',
    'shooting_other',
  ],
}

const eventNames = {
  time: 'Time',
  split_time: 'Split TIme',
  combined_time: 'Time',
  combined_downhill_time: 'Downhill Time',
  combined_downhill_speed: 'Downhill Speed',
  downhill_time: 'Time',
  downhill_speed: 'Downhill Speed',
  downhill_split_time: 'Split Time',
  combined_slalom_time: 'Slalom Time',
  yellow_shirt: 'Yellow Shirt',
  green_shirt: 'Green Shirt',
  climber_shirt: 'Climber Shirt',
  youth_shirt: 'Youth Shirt',
  aggressive: 'Most Aggressive',
  ping: 'Ping',
  shooting_other: 'Shooting Event',
  shooting_relay: 'Shooting Event',
  auxiliary1: 'Auxiliary 1',
  auxiliary2: 'Auxiliary 2',
  auxiliary3: 'Auxiliary 3',
}

@Component('sporty')
@Template(template)
@Attribute('match', Match)
@Attribute('edit-mode', Boolean)
@Attribute('search', String)
@Attribute('time', String)
@Attribute('event-type', String)
@Attribute('batch', Number)
@Attribute('position-override', Number)
@Attribute('misses', Number)
@Attribute('selected', Boolean)
export default class TimeTrialResults extends HTMLElement {
  attached() {
    this.update(this.match.load()).then(() => {
      if(!this.events) this.events = []
    })
    this.state.listen(this, true)
  }

  reset() {
    this.state.reset()
  }

  matchChange() {
    this.update(this.match.load())
    this.state.listen(this, true)
  }

  saveStats() {
    var lineups = this.match.lineups.map(lineup => {
      if(lineup.stats.splitRank) {
        lineup.stats.manualEntry = true
      }
      return {
        _id: lineup._id,
        match: lineup.matchId,
        player: lineup.playerId,
        team: lineup.teamId,
        stats: lineup.stats,
        status: lineup.status,
        duration: lineup.duration,
        trainer: lineup.trainer,
        rider: lineup.rider,
      }
    })

    this.match.post('lineups', { lineups })
      .then(() => {
        notify('Stats saved', { level: 'success', timeout: 3000 })
        this.state.reload(true)
        emitter.emit('match-updated')
        this.editMode = false
      })
  }

  canScrapeLineups(source) {
    var s = sources[source.source]
    return s && s.lineups
  }

  scrapeLineups(source) {
    source.post('scrape').then(() => {
      notify('Lineups gathered', { level: 'success', timeout: 5000 })
      this.state.reload(true)
    }).catch((err) => {
      console.error(err.stack)
      notify(err.message, { level: 'danger', timeout: 10000 })
    })
  }

  setMisses(val) {
    this.misses = val
    this.time = null
  }

  get canComputeAggregate() {
    const { sport, matchType, difficulty } = this.match || {}
    return sport === 'cc_ski' && matchType === 'heat' && (difficulty || 0) < 3
  }

  computeAggregate() {
    if(this.match.$isSubmitting) return
    this.match.post('aggregate')
      .then(() => {
        notify('Stats computed', { level: 'success', timeout: 5000 })
      })
      .catch((err) => {
        notify(err.message, { level: 'danger', timeout: 10000 })
      })
      .then(() => this.render())
  }

  sendFeeds() {
    if(this.match.$isSubmitting) return
    this.match.post('send-feeds', { forceCalculations: true })
      .then(() => { notify('Events sent', { level: 'success', timeout: 5000 }) })
      .catch((err) => {
        notify(err.message, { level: 'danger', timeout: 10000 })
      })
      .then(() => this.render())
    this.render()
  }

  saveSettings() {
    if(this.match.$isSubmitting) return
    this.match.save({
      match: {
        difficulty: this.match.difficulty,
        sprints: this.match.sprints,
      },
    })
      .then(() => {
        notify('Match updated', { level: 'success', timeout: 5000 })
      })
      .catch((err) => {
        notify(err.message, { level: 'danger', timeout: 10000 })
      })
      .then(() => this.render())
    this.render()
  }

  async destroyAllEvents(eventsByName, noPrompt) {
    this.isSubmittingRank = true
    if(eventsByName.map(z => z.name).includes('match_started')) {
      return
    }
    if(!noPrompt) {
      const answer = await prompt(`
          This will attempt to delete all events belonging to this match.
          Please write "I know what I'm doing" to continue.
        `, {
        level: 'warning',
      }).catch(() => '')

      if(answer.toLowerCase() !== "i know what i'm doing") {
        if(answer.length) prompt('Confirmation text was not correct', { level: 'danger', type: 'ok' })
        return
      }
    }
    var lineups = this.match.lineups.map(lineup => {
      lineup.stats.manualEntry = false
      return {
        _id: lineup._id,
        match: lineup.matchId,
        player: lineup.playerId,
        team: lineup.teamId,
        stats: lineup.stats,
        status: lineup.status,
        duration: lineup.duration,
        trainer: lineup.trainer,
        rider: lineup.rider,
      }
    })
    this.match.post('lineups', { lineups })
      .then(() => {
        notify('Stats saved', { level: 'success', timeout: 3000 })
        this.state.reload(true)
        emitter.emit('match-updated')
        this.editMode = false
        this.render()
      })

    this.match.post('destroy-all-events', { eventsByName }).then(() => {
      this.state.reload()
      this.isSubmittingRank = false
      this.render()
    }).catch(() => {
      this.render()
    })
    this.render()
  }

  toggleSelected(event) {
    event.selected = !event.selected
    this.render()
    setTimeout(() => this.render(), 200)
  }

  async destroySelectedEvents(selectedEvents) {
    var eventsByName = []
    for(const event of selectedEvents) {
      if(!event.selected) continue
      else {
        this.render()
        eventsByName.push(event)
      }
    }
    if(eventsByName.length === 0) {
      await prompt('No events selected', { level: 'danger', type: 'ok' })
      return
    }
    this.destroyAllEvents(eventsByName)
  }

  destroy(event) {
    this.destroyAllEvents([event], true)
  }

  isResult(event) {
    return event.name === 'finished' ||
      !event.name.indexOf('top') ||
      ~event.name.indexOf('rank') ||
      ~event.name.indexOf('Rank')
  }

  eventName(event) {
    return eventNames[event]
  }

  eventClass(event) {
    if((this.eventType || 'rank') === event) return 'selected'
  }

  isSelected(val) {
    if(this.misses === val) return 'selected'
  }
  selectEventType(event) {
    this.eventType = event
    if(event === 'shooting_other') this.time = null
    if(event === 'shooting_relay') this.time = null
  }

  get state() {
    return getState(this.match)
  }

  get nextPosition() {
    const attr =
      this.eventType && this.eventType.includes('_rank') ? camelCase(this.eventType)
        : camelCase(this.eventTypes[0])
    if(/shooting/.test(this.eventType)) {
      return 1
    }
    return (this.lineups || [])
      .filter(l => l.stats && l.stats[attr])
      .filter(l => !this.batch || this.batch === (l.batch || l.stats.batch))
      .reduce((max, l) => Math.max(max, l.stats[attr]), 0) + 1
  }

  get unranked() {
    const search = (this.search || '')
      .split(/\s+|,/)
      .filter(part => part)
      .map(normalize)
    const attributed = new Set()
    const type = this.match.matchType
    const eventType = this.eventType ||
      (type == 'combined' ? 'combined_time'
        : type === 'downhill' ? 'downhill_time'
          : 'time')
    for(const event of (this.events || [])) {
      if(event.name !== eventType) continue
      if(['shooting_other', 'shooting_relay'].includes(eventType)) continue

      attributed.add(event.player)
    }

    return (this.lineups || [])
      .filter(l => l.status === 'confirmed')
      .filter(l => !~['manager', 'rider', 'coach'].indexOf(l.player.position))

      .filter(l => !attributed.has(l.player))
      .filter(l => {
        if(!search.length) return true
        const searched = [
          l.player.firstName,
          l.player.lastName,
          l.player.customName,
          l.team && l.team.name,
          l.team && l.team.abbr,
        ]
          .filter(part => part)
          .map(normalize)
          .join('|')
        for(const part of search) {
          if(~searched.indexOf(part)) return true
        }
      })
      .sort((a, b) => a.player.name.localeCompare(b.player.name))
  }

  get cantSubmit() {
    return (this.eventType || 'time') === 'time' ? !this.time : false
  }

  get isShooting() {
    return /shooting/.test(this.eventType)
  }
  addTime(lineup) {
    if(this.isSubmittingRank) return
    if(!lineup.stats) lineup.stats = {}
    const type = this.match.matchType
    const eventType = this.eventType ||
      (type == 'combined' ? 'combined_time'
        : type === 'downhill' ? 'downhill_time'
          : 'time')

    var minute, second, centi
    if(this.time) {
      const segments = this.time.split(/[,:. ]/)
      if(segments.length === 1) {
        [minute] = segments
        second = 0
        centi = 0
      } else if(segments.length === 2) {
        [minute, second] = segments
        centi = 0
      } else if(segments.length === 3) {
        [minute, second, centi] = segments
      } else {
        notify(`Unable to parse: ${this.time}`, { level: 'danger', timeout: 5000 })
        return
      }
    } else {
      minute = 10000
      second = 0
      if(this.isShooting) {
        minute = this.misses == null ? 0 : this.misses
      }
    }

    lineup.$isSubmitting = true
    this.isSubmittingRank = true
    const existing = this.events
      .filter(e => e.name === eventType)
      .length
    const event = {
      second: second,
      minute: this.isShooting ? minute : 1000 * existing + +minute,
      name: eventType,
      match: this.match._id,
      team: lineup.team._id,
      player: lineup.player._id,
      duration: centi,
    }
    if(eventType === 'ping') {
      delete event.player
      delete event.team
    }
    this.match
      .post('events', {
        event: event,
        skipCalculations: true,
      })
      .catch(err => console.error(err))
      .then(({ event: createdEvent }) => {
        this.events.push(Event.instance(createdEvent._id, event))
        this.isSubmittingRank = false
        lineup.$isSubmitting = false
        this.render()
      })
    if(!this.isShooting) {
      this.time = `${+minute + 1}:00`
    }
    this.render()
  }

  get eventTypes() {
    const { sport, matchType } = this.match
    let category
    if(matchType === 'normal') {
      category = sport
    } else if(/\b(stage)\b/.test(this.match.tag)) {
      category = sport + '_' + matchType + '_' + 'stage'
    } else {
      category = sport + '_' + matchType
    }
    if(!matchType) {
      category = sport
    }
    return eventTypes[category] || ['time', 'split_time']
  }

  get rankType() {
    return this.eventTypes[0].replace(/time$/, 'rank')
  }

  batched(batch) {
    return (this.lineups && this.lineups.filter(l => l.batch === batch)) || []
  }

  showManager(match) {
    if(match.sport === 'cycling' && !match.matchType) return true
    if(!match.isRacing) return false
    if(/\b(prolog|semi|quarter|stage)\b/.test(match.tag)) return false
    return true
  }

  timestamp(event) {
    if(/_speed/.exec(event.name)) {
      return (event.minute % 1000) + ':' +
        ('' + (event.second || 0)).padStart(2, '0') + 'km/h'
    }
    return (event.minute % 1000) + ':' +
      ('' + (event.second || 0)).padStart(2, '0') + '.' +
      ('' + (event.duration || 0)).padStart(2, '0')
  }

  get hasShootingEvents() {
    return this.match.sport === 'biathlon'
  }

  get nameSortedEvents() {
    const eventsByName = []
    if(!this.events) return
    const eventTypes = this.eventTypes
      .concat('match_started', 'match_ended')
      .filter(type => !~type.indexOf('_shirt') && type !== 'aggressive')
    const events = this.events || []
    if(this.match.sport === 'cycling') {
      const filteredEvents = events
        .filter(e => ~e.name.indexOf('_shirt') || e.name == 'aggressive')
      eventsByName.push(filteredEvents)
    }
    for(const eventType of eventTypes) {
      if(eventType === 'rank') {
        const filteredEvents = events
          .filter(e => {
            return (
              e.name.indexOf(eventType) !== -1 &&
              e.name.indexOf('split') === -1
            ) || e.name.indexOf('top') === 0 ||
              e.name === 'finished'
          })
          .sort((a, b) => b.minute - a.minute)
        eventsByName.push(filteredEvents)
      } else if(eventType.indexOf('time') + 1) {
        const filteredEvents = events
          .filter(e => e.name === eventType)
          .sort((b, a) => {
            return (
              ((a.minute % 1000) - (b.minute % 1000)) ||
              ((a.second || 0) - (b.second || 0)) ||
              ((a.duration || 0) - (b.duration || 0))
            )
          })
        eventsByName.push(filteredEvents.reverse())
      } else {
        const filteredEvents = events.filter(e => e.name.indexOf(eventType) !== -1)
        eventsByName.push(filteredEvents)
      }
    }
    return eventsByName.filter(arr => arr.length)
  }
  concatEvents(allEvents) {
    var arr = []
    for(const events of allEvents) {
      for(const singleEvent of events) {
        arr.push(singleEvent)
      }
    }
    return arr
  }
}
